import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { UserCircle, Loader2, KeyRound, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { supabase, isDemoMode } from '@/lib/supabase';
import type { Organization, SubscriptionPlan } from '@/types';
import { PLAN_MEMBER_LIMITS } from '@/types';
import { toast } from 'sonner';
import { sendWelcomeNotification } from '@/lib/notifications';

export default function MemberSignup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [existingMember, setExistingMember] = useState<{
    id: string;
    full_name: string;
    email: string | null;
    phone: string | null;
  } | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);

  const [formData, setFormData] = useState({
    joinCode: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
    // Clear organization when join code changes
    if (field === 'joinCode') {
      setOrganization(null);
      setExistingMember(null);
    }
    if (field === 'email' || field === 'phone') {
      setExistingMember(null);
    }
  };

  const lookupExistingMember = async (email: string, phone: string) => {
    if (!organization) return null;

    const normalizedEmail = email.trim();
    const normalizedPhone = phone.trim();

    if (!normalizedEmail && !normalizedPhone) {
      setExistingMember(null);
      return null;
    }

    const matches = new Map<string, { id: string; full_name: string; email: string | null; phone: string | null }>();

    if (normalizedEmail) {
      const { data: emailMatches, error: emailError } = await supabase
        .from('members')
        .select('id, full_name, email, phone')
        .eq('organization_id', organization.id)
        .eq('email', normalizedEmail)
        .limit(2);

      if (emailError) throw emailError;
      (emailMatches || []).forEach((member) => {
        matches.set(member.id, member);
      });
    }

    if (normalizedPhone) {
      const { data: phoneMatches, error: phoneError } = await supabase
        .from('members')
        .select('id, full_name, email, phone')
        .eq('organization_id', organization.id)
        .eq('phone', normalizedPhone)
        .limit(2);

      if (phoneError) throw phoneError;
      (phoneMatches || []).forEach((member) => {
        matches.set(member.id, member);
      });
    }

    if (matches.size > 1) {
      throw new Error('Multiple member records matched your details. Please contact your organization admin.');
    }

    const existingData = matches.values().next().value ?? null;
    setExistingMember(existingData);
    return existingData;
  };

  const handleContactBlur = async () => {
    if (!organization || isDemoMode) return;
    if (!formData.email.trim() && !formData.phone.trim()) return;

    setIsCheckingExisting(true);
    try {
      await lookupExistingMember(formData.email, formData.phone);
    } catch {
      // Ignore lookup errors for pre-submit helper feedback
    } finally {
      setIsCheckingExisting(false);
    }
  };

  const verifyJoinCode = async () => {
    if (!formData.joinCode.trim()) {
      setError('Please enter a join code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      if (isDemoMode) {
        // Demo mode - simulate verification
        await new Promise((resolve) => setTimeout(resolve, 800));
        setOrganization({
          id: 'demo-org-id',
          name: 'Demo Organization',
          currency: 'USD',
          language: 'en',
          monthly_dues_amount: 0,
          created_by: 'demo',
        });
        toast.success('Organization found!');
        return;
      }

      const { data, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('join_code', formData.joinCode.toUpperCase())
        .single();

      if (orgError || !data) {
        throw new Error('Invalid join code. Please check and try again.');
      }

      setOrganization(data);
      toast.success(`Found: ${data.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid join code. Please check and try again.');
      setOrganization(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organization) {
      setError('Please verify your join code first');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isDemoMode) {
        // Demo mode - simulate signup
        await new Promise((resolve) => setTimeout(resolve, 1500));
        toast.success('Demo: Account created successfully!');
        navigate('/member-login');
        return;
      }

      // 1. Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Account creation failed');

      // If email confirmation is required, there will be no session yet.
      // Inform the user and skip DB inserts — they will be completed after login.
      if (!authData.session) {
        toast.success(`Account created! Please check your email to verify your account, then sign in to complete joining ${organization.name}.`);
        navigate('/member-login');
        return;
      }

      // 2. Link to existing member record if found, otherwise create a new one
      const existingData = await lookupExistingMember(formData.email, formData.phone);

      if (existingData) {
        const safeMemberName = existingData.full_name
          .replace(/[^\p{L}\p{N}\s'.-]/gu, '')
          .trim() || 'member';
        if (formData.email && existingData.email !== formData.email) {
          const { error: updateEmailError } = await supabase
            .from('members')
            .update({ email: formData.email })
            .eq('id', existingData.id);

          if (updateEmailError) throw updateEmailError;
        }

        toast.success(`Welcome back, ${safeMemberName}! Your account has been linked to your existing record.`);
      } else {
        // Check member cap only when creating a new member record
        const orgPlan = (organization.subscription_plan ?? 'free') as SubscriptionPlan;
        const memberLimit = PLAN_MEMBER_LIMITS[orgPlan];
        const { count, error: countError } = await supabase
          .from('members')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organization.id);

        if (!countError && count !== null && count >= memberLimit) {
          throw new Error(
            `This organization has reached its ${memberLimit}-member limit on the ${orgPlan} plan. Please contact the organization admin.`
          );
        }

        const { error: memberError } = await supabase.from('members').insert({
          organization_id: organization.id,
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          join_date: new Date().toISOString().split('T')[0],
          status: 'active',
          dues_balance: 0,
          credit_balance: 0,
        });

        if (memberError) throw memberError;
      }

      // 3. Create user_organizations entry with member role
      const { error: userOrgError } = await supabase.from('user_organizations').insert({
        user_id: authData.user.id,
        organization_id: organization.id,
        role: 'member',
        is_active: true,
      });

      if (userOrgError) throw userOrgError;

      await sendWelcomeNotification(
        authData.user.id,
        organization.id,
        existingData?.full_name || formData.fullName
      );

      toast.success(t('auth.registerSuccess'));
      navigate('/member-login');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.registerError');
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-500/10 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <UserCircle className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold">{t('common.appName')}</span>
          </Link>
          <p className="mt-2 text-muted-foreground">{t('public.home.members.title')}</p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">
              {t('auth.createAccount')}
            </CardTitle>
            <CardDescription className="text-center">
              Join your organization with a join code
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Join Code */}
              <div className="space-y-2">
                <Label htmlFor="joinCode">
                  <KeyRound className="inline h-4 w-4 mr-1" />
                  Organization Join Code *
                </Label>
                <div className="flex space-x-2">
                  <Input
                    id="joinCode"
                    placeholder="e.g., ABC12345"
                    value={formData.joinCode}
                    onChange={(e) => handleChange('joinCode', e.target.value.toUpperCase())}
                    className="uppercase"
                    maxLength={8}
                    disabled={!!organization}
                  />
                  {!organization ? (
                    <Button
                      type="button"
                      onClick={verifyJoinCode}
                      disabled={isVerifying || !formData.joinCode.trim()}
                      variant="outline"
                    >
                      {isVerifying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setOrganization(null);
                        setFormData((prev) => ({ ...prev, joinCode: '' }));
                      }}
                    >
                      Change
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ask your organization admin for the join code
                </p>
              </div>

              {/* Organization Found */}
              {organization && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-700">
                    Joining: <strong>{organization.name}</strong>
                    {existingMember && (
                      <span className="block mt-2">
                        <Badge className="bg-green-600 hover:bg-green-600 text-white mr-2">Linked</Badge>
                        Found existing record: <strong>{existingMember.full_name}</strong>
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {organization && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="fullName">
                      {t('auth.fullName')} *
                    </Label>
                    <Input
                      id="fullName"
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t('auth.email')} *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={handleContactBlur}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      {t('common.phone')}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 234 567 8900"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      onBlur={handleContactBlur}
                    />
                  </div>

                  {isCheckingExisting && (
                    <p className="text-xs text-muted-foreground">Checking for existing record...</p>
                  )}

                  {existingMember && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <AlertDescription className="text-green-700">
                        We found an existing record for <strong>{existingMember.full_name}</strong>. Your account will be linked automatically.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">
                      {t('auth.password')} *
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Minimum 6 characters
                    </p>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !organization}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.createAccount')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link
                  to="/member-login"
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signIn')}
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
